import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import type {
  TwilioIntegration,
  TwilioMode,
  TwilioRegion,
} from "@chat-yai/shared/admin-types";
import { integrationsApi } from "@/api/endpoints/integrations";
import { Modal } from "@/components/Modal";
import { FormError } from "@/components/FormError";
import { useToast } from "@/contexts/ToastContext";
import { ApiError } from "@/api/client";
import { translateError } from "@/i18n/errors";
import { useSession } from "@/contexts/SessionContext";
import { canManageContent } from "@/lib/rbac";

const REGIONS: { value: TwilioRegion; label: string }[] = [
  { value: "au1", label: "Australia (au1)" },
  { value: "us1", label: "United States (us1)" },
  { value: "ie1", label: "Ireland (ie1)" },
  { value: "de1", label: "Germany (de1)" },
  { value: "jp1", label: "Japan (jp1)" },
  { value: "br1", label: "Brazil (br1)" },
];

const MODES: { value: TwilioMode; label: string; help: string }[] = [
  {
    value: "customer_twilio",
    label: "Customer Twilio (BYO)",
    help: "Bring your own Twilio account. Recommended for production.",
  },
  {
    value: "managed_twilio",
    label: "Managed Twilio (shared pool)",
    help: "Use BestAI's shared number pool. Coming soon.",
  },
];

interface FormValues {
  mode: TwilioMode;
  account_sid: string;
  auth_token: string;
  region: TwilioRegion;
}

const ACCOUNT_SID_RE = /^AC[a-zA-Z0-9]{32}$/;

export function IntegrationsTwilioPage(): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { me } = useSession();
  const role =
    me?.memberships.find((m) => m.tenant_id === me.current_tenant_id)?.role ??
    "viewer";
  const canEdit = canManageContent(role);
  const canDelete = role === "owner";

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteAck, setDeleteAck] = useState(false);
  const [testResult, setTestResult] = useState<
    { ok: boolean; message: string } | null
  >(null);

  const twilioQuery = useQuery({
    queryKey: ["integrations", "twilio"],
    queryFn: () => integrationsApi.getTwilio(),
  });

  const { register, handleSubmit, reset, watch, formState } = useForm<FormValues>({
    defaultValues: {
      mode: "customer_twilio",
      account_sid: "",
      auth_token: "",
      region: "au1",
    },
  });

  useEffect(() => {
    const data = twilioQuery.data;
    if (!data) return;
    reset({
      mode: data.mode,
      account_sid: data.account_sid ?? "",
      auth_token: "",
      region: data.region,
    });
  }, [twilioQuery.data, reset]);

  const saveMutation = useMutation({
    mutationFn: (input: FormValues) => integrationsApi.putTwilio(input),
    onSuccess: (next) => {
      queryClient.setQueryData(["integrations", "twilio"], next);
      toast.push("success", "Twilio configuration saved.");
      reset({
        mode: next.mode,
        account_sid: next.account_sid ?? "",
        auth_token: "",
        region: next.region,
      });
    },
    onError: (err) => {
      toast.push(
        "error",
        translateError(err instanceof ApiError ? err.code : undefined),
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => integrationsApi.deleteTwilio(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["integrations", "twilio"] });
      queryClient.invalidateQueries({ queryKey: ["phone-numbers"] });
      toast.push("success", "Twilio configuration removed.");
      setConfirmDelete(false);
      setDeleteAck(false);
    },
    onError: (err) => {
      toast.push(
        "error",
        translateError(err instanceof ApiError ? err.code : undefined),
      );
    },
  });

  const accountSidValue = watch("account_sid");

  const onTestConnection = (): void => {
    if (!ACCOUNT_SID_RE.test(accountSidValue.trim())) {
      setTestResult({
        ok: false,
        message:
          "Account SID must look like ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx (34 chars, starts with AC).",
      });
      return;
    }
    setTestResult({
      ok: true,
      message:
        "Account SID format looks correct. Save the configuration to verify against Twilio.",
    });
  };

  const data: TwilioIntegration | undefined = twilioQuery.data;

  return (
    <div className="page" data-testid="integrations-twilio-page">
      <div className="page-head">
        <div>
          <div className="page-title">Twilio integration</div>
          <div className="page-sub">
            Connect a Twilio account to power phone calls. Auth tokens are
            encrypted at rest; the admin UI only ever sees a masked preview.
          </div>
        </div>
      </div>

      {twilioQuery.isLoading || !data ? (
        <div className="empty">Loading…</div>
      ) : (
        <>
          <div className="card" style={{ padding: 14, marginBottom: 12 }}>
            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                flexWrap: "wrap",
              }}
              data-testid="twilio-status"
            >
              <span
                className={
                  "chip " +
                  (data.configured ? "chip-success" : "chip-warning")
                }
              >
                {data.configured ? "Connected" : "Not configured"}
              </span>
              {data.configured && data.verified_at ? (
                <span className="help" style={{ margin: 0 }}>
                  Verified {new Date(data.verified_at).toLocaleString()}
                </span>
              ) : null}
              <span className="help" style={{ margin: 0 }}>
                Phone numbers attached: {data.phone_numbers_count}
              </span>
              {data.auth_token_masked ? (
                <span
                  className="mono help"
                  style={{ margin: 0 }}
                  data-testid="twilio-token-masked"
                >
                  Auth token: {data.auth_token_masked}
                </span>
              ) : null}
            </div>
          </div>

          <form
            className="card"
            style={{
              padding: 18,
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
            onSubmit={handleSubmit((v) => saveMutation.mutate(v))}
            data-testid="twilio-form"
          >
            <fieldset
              disabled={!canEdit || saveMutation.isPending}
              style={{ border: 0, padding: 0, display: "contents" }}
            >
              <label className="field">
                <span className="field-label">Mode</span>
                <select
                  className="field-input"
                  data-testid="twilio-mode"
                  {...register("mode")}
                >
                  {MODES.map((m) => (
                    <option
                      key={m.value}
                      value={m.value}
                      disabled={m.value === "managed_twilio"}
                    >
                      {m.label}
                    </option>
                  ))}
                </select>
                <span className="help">
                  {MODES.find((m) => m.value === watch("mode"))?.help}
                </span>
              </label>

              <label className="field">
                <span className="field-label">Account SID</span>
                <input
                  className="field-input mono"
                  placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  data-testid="twilio-account-sid"
                  {...register("account_sid", { required: true })}
                />
                <FormError
                  message={
                    formState.errors.account_sid
                      ? "Account SID is required."
                      : undefined
                  }
                />
              </label>

              <label className="field">
                <span className="field-label">Auth Token</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  className="field-input mono"
                  placeholder={
                    data.auth_token_masked
                      ? "Leave blank to keep existing token"
                      : "your-twilio-auth-token"
                  }
                  data-testid="twilio-auth-token"
                  {...register("auth_token", {
                    required: !data.configured,
                  })}
                />
                <span className="help">
                  Stored encrypted. The admin UI never displays the full
                  token after saving.
                </span>
              </label>

              <label className="field" style={{ maxWidth: 320 }}>
                <span className="field-label">Region</span>
                <select
                  className="field-input"
                  data-testid="twilio-region"
                  {...register("region")}
                >
                  {REGIONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </label>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="submit"
                  className="btn btn-accent btn-sm"
                  disabled={!canEdit || saveMutation.isPending}
                  data-testid="twilio-save"
                >
                  {saveMutation.isPending
                    ? "Saving…"
                    : data.configured
                      ? "Update configuration"
                      : "Connect Twilio"}
                </button>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={onTestConnection}
                  disabled={!canEdit}
                  data-testid="twilio-test-connection"
                >
                  Test connection
                </button>
                {!canEdit ? (
                  <span className="help" style={{ alignSelf: "center" }}>
                    Read-only — Admin or Owner can change these settings.
                  </span>
                ) : null}
              </div>

              {testResult ? (
                <div
                  className={
                    "chip " +
                    (testResult.ok ? "chip-success" : "chip-warning")
                  }
                  data-testid="twilio-test-result"
                  style={{ alignSelf: "flex-start" }}
                >
                  {testResult.message}
                </div>
              ) : null}
            </fieldset>
          </form>

          {data.configured && canDelete ? (
            <div className="card" style={{ padding: 18, marginTop: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                Danger zone
              </div>
              <div className="help" style={{ marginTop: 0 }}>
                Removing the Twilio configuration releases all attached phone
                numbers and disables phone calls for this workspace.
              </div>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => setConfirmDelete(true)}
                data-testid="twilio-delete"
                style={{ marginTop: 8 }}
              >
                Delete configuration
              </button>
            </div>
          ) : null}

          <Modal
            title="Remove Twilio configuration"
            open={confirmDelete}
            onClose={() => {
              setConfirmDelete(false);
              setDeleteAck(false);
            }}
            footer={
              <>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    setConfirmDelete(false);
                    setDeleteAck(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  disabled={!deleteAck || deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate()}
                  data-testid="twilio-delete-confirm"
                >
                  {deleteMutation.isPending ? "Removing…" : "Remove configuration"}
                </button>
              </>
            }
          >
            <p>
              This will release all{" "}
              <strong>{data.phone_numbers_count}</strong> attached phone
              numbers and disable inbound calls. The Twilio account itself
              is unaffected.
            </p>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 8,
              }}
            >
              <input
                type="checkbox"
                checked={deleteAck}
                onChange={(e) => setDeleteAck(e.target.checked)}
                data-testid="twilio-delete-ack"
              />
              <span style={{ fontSize: 13 }}>
                I understand attached numbers will be released.
              </span>
            </label>
          </Modal>
        </>
      )}
    </div>
  );
}
